/**
 * Admin Subscription Management Service
 * API: /api/admin/subscriptions
 */

import client from '@lib/core/axios.client';
import { API_ENDPOINTS } from '@lib/constants';
import type { RuntimeQuotaUsage } from '@/types/api/subscription.types';
import type {
  AdminPlanListParams,
  SubscriptionPlanListItemDto,
  SubscriptionPlanDto,
  CreateSubscriptionPlanCommand,
  UpdateSubscriptionPlanCommand,
  AdminTenantSubscriptionListResponse,
  TenantSubscriptionDto,
  CurrentSubscriptionDto,
  SubscriptionUsageDto,
  CreateSubscriptionRequest, 
  UpgradeSubscriptionRequest, 
  RenewSubscriptionRequest,
  CancelSubscriptionRequest,
  SuspendSubscriptionRequest,
  UpdateSubscriptionEndDateRequest,
  CreateCustomPlanRequest,
  PendingSubscriptionEntitlementDto,
  PreviewPlanFeatureChangesRequest,
  PlanFeatureChangeImpactPreviewDto,
  UpdatePlanFeatureRequest,
} from '@/types/api/admin';

export class AdminSubscriptionService {
  private static instance: AdminSubscriptionService;

  private constructor() {}

  public static getInstance(): AdminSubscriptionService {
    if (!AdminSubscriptionService.instance) {
      AdminSubscriptionService.instance = new AdminSubscriptionService();
    }
    return AdminSubscriptionService.instance;
  }

  // ============ Plans ============

  /**
   * Get list of all subscription plans
   */
  async getPlans(params?: AdminPlanListParams): Promise<SubscriptionPlanListItemDto[]> {
    const response = await client.get<SubscriptionPlanListItemDto[]>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.PLANS.LIST,
      { params }
    );
    return response;
  }

  /**
   * Get plan by ID
   */
  async getPlanById(planId: string): Promise<SubscriptionPlanDto> {
    return await client.get<SubscriptionPlanDto>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.PLANS.BY_ID(planId)
    );
  }

  /**
   * Create new subscription plan
   */
  async createPlan(data: CreateSubscriptionPlanCommand): Promise<SubscriptionPlanListItemDto> {
    return await client.post<SubscriptionPlanListItemDto>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.PLANS.CREATE,
      data
    );
  }

  async createCustomPlan(data: CreateCustomPlanRequest): Promise<SubscriptionPlanDto> {
    return await client.post<SubscriptionPlanDto>(
      '/api/admin/subscriptions/plans/custom',
      data
    );
  }

  async getPendingEntitlementChanges(params?: { planId?: string; tenantId?: string }): Promise<PendingSubscriptionEntitlementDto[]> {
    return await client.get<PendingSubscriptionEntitlementDto[]>(
      '/api/admin/subscriptions/entitlements/pending',
      { params }
    );
  }

  async previewPlanFeatureChanges(
    planId: string,
    data: PreviewPlanFeatureChangesRequest
  ): Promise<PlanFeatureChangeImpactPreviewDto> {
    return await client.post<PlanFeatureChangeImpactPreviewDto>(
      `/api/admin/subscriptions/plans/${planId}/features/impact-preview`,
      data
    );
  }

  async updatePlanFeatures(
    planId: string,
    features: UpdatePlanFeatureRequest[]
  ): Promise<SubscriptionPlanDto> {
    return await client.put<SubscriptionPlanDto>(
      `/api/admin/subscriptions/plans/${planId}/features`,
      features
    );
  }

  /**
   * Update existing subscription plan
   */
  async updatePlan(planId: string, data: UpdateSubscriptionPlanCommand): Promise<SubscriptionPlanListItemDto> {
    return await client.put<SubscriptionPlanListItemDto>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.PLANS.UPDATE(planId),
      data
    );
  }

  /**
   * Delete subscription plan
   */
  async deletePlan(planId: string): Promise<void> {
    return await client.delete<void>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.PLANS.DELETE(planId)
    );
  }

  // ============ Tenant Subscriptions ============

  /**
   * Get list of all tenant subscriptions
   */
  async getTenantSubscriptions(params?: {
    page?: number;
    pageSize?: number;
    tenantId?: string;
    planId?: string;
    status?: number;
    search?: string;
    currentOnly?: boolean;
  }): Promise<AdminTenantSubscriptionListResponse> {
    return await client.get<AdminTenantSubscriptionListResponse>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.TENANT_SUBSCRIPTIONS.LIST,
      { params }
    );
  }

  /**
   * Get tenant subscription by ID
   */
  async getTenantSubscriptionById(subscriptionId: string): Promise<TenantSubscriptionDto> {
    return await client.get<TenantSubscriptionDto>(
      API_ENDPOINTS.ADMIN.SUBSCRIPTIONS.TENANT_SUBSCRIPTIONS.BY_ID(subscriptionId)
    );
  }

  // ============ Tenant Subscription Operations ============

  /**
   * Get tenant's current subscription
   */
  async getTenantSubscription(tenantId: string): Promise<CurrentSubscriptionDto> {
    return await client.get<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}`
    );
  }

  /**
   * Get tenant's recorded subscription usage
   */
  async getTenantUsage(tenantId: string): Promise<SubscriptionUsageDto[]> {
    return await client.get<SubscriptionUsageDto[]>(
      `/api/admin/subscriptions/tenants/${tenantId}/usage`
    );
  }

  /**
   * Get tenant's Partner-owned Operator and Copilot quota usage.
   */
  async getTenantRuntimeUsage(tenantId: string): Promise<RuntimeQuotaUsage> {
    return await client.get<RuntimeQuotaUsage>(
      `/api/admin/subscriptions/tenants/${tenantId}/runtime-usage`
    );
  }

  /**
   * Create subscription for tenant
   */
  async createTenantSubscription(tenantId: string, data: CreateSubscriptionRequest): Promise<CurrentSubscriptionDto> {
    return await client.post<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}/subscription`,
      data
    );
  }

  /**
   * Upgrade tenant subscription to new plan
   */
  async upgradeTenantSubscription(tenantId: string, data: UpgradeSubscriptionRequest): Promise<CurrentSubscriptionDto> {
    return await client.post<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}/upgrade`,
      data
    );
  }

  /**
   * Renew tenant subscription
   */
  async renewTenantSubscription(tenantId: string, data?: RenewSubscriptionRequest): Promise<CurrentSubscriptionDto> {
    return await client.post<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}/renew`,
      data
    );
  }

  /**
   * Cancel tenant subscription
   */
  async cancelTenantSubscription(tenantId: string, data?: CancelSubscriptionRequest): Promise<void> {
    return await client.post<void>(
      `/api/admin/subscriptions/tenants/${tenantId}/cancel`,
      data
    );
  }

  /**
   * Suspend tenant subscription
   */
  async suspendTenantSubscription(tenantId: string, data?: SuspendSubscriptionRequest): Promise<void> {
    return await client.post<void>(
      `/api/admin/subscriptions/tenants/${tenantId}/suspend`,
      data
    );
  }

  /**
   * Reactivate suspended tenant subscription
   */
  async reactivateTenantSubscription(tenantId: string): Promise<void> {
    return await client.post<void>(
      `/api/admin/subscriptions/tenants/${tenantId}/reactivate`
    );
  }

  /**
   * Create a Free subscription for tenant
   */
  async createFreeSubscription(tenantId: string): Promise<CurrentSubscriptionDto> {
    return await client.post<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}/free`
    );
  }

  /**
   * Manually update end date of tenant's active subscription
   */
  async updateSubscriptionEndDate(tenantId: string, data: UpdateSubscriptionEndDateRequest): Promise<CurrentSubscriptionDto> {
    return await client.put<CurrentSubscriptionDto>(
      `/api/admin/subscriptions/tenants/${tenantId}/end-date`,
      data
    );
  }
}

export const adminSubscriptionService = AdminSubscriptionService.getInstance();
export default adminSubscriptionService;
